import React from 'react';

import Avatar, { AvatarItem } from '@atlaskit/avatar';
import Badge from '@atlaskit/badge';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import Tooltip from '@atlaskit/tooltip';
import { UserProfileCardWithTrigger } from '@atlassian/user-profile-card/trigger';

import { AvatarItemContainer, AvatarItemText, ChartCard } from './styles';
import { type HierarchyUser } from './types';

interface HierarchyUserCardProps {
	user: HierarchyUser;
	selected?: boolean;
	testId?: string;
	onNodeClick?: (accountId: string) => void;
	cloudId?: string;
	requestingUserId?: string;
}

const NoopComponent = () => null;

/**
 * Card component for displaying a single user in the hierarchy chart.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const HierarchyUserCard: React.FC<HierarchyUserCardProps & React.RefAttributes<unknown>> =
	componentWithFG('pt_hierarchy_chart_enabled', NoopComponent, HierarchyUserCardComponent);

function HierarchyUserCardComponent({
	user,
	selected = false,
	testId,
	onNodeClick,
	cloudId,
	requestingUserId,
}: HierarchyUserCardProps) {
	const { name, avatarUrl, jobTitle, numberOfDirectReports = 0, numberOfTotalReports = 0 } = user;
	const label = `${name}${jobTitle ? ` (${jobTitle})` : ''}`;

	const handleClick = onNodeClick ? () => onNodeClick(user.accountId) : undefined;

	const card = (
		<ChartCard selected={selected} testId={testId} onClick={handleClick}>
			<AvatarItemContainer>
				<AvatarItem
					avatar={<Avatar name={name} src={avatarUrl ?? undefined} label={label} />}
					primaryText={name && <AvatarItemText>{name}</AvatarItemText>}
					secondaryText={
						jobTitle && (
							<Tooltip content={jobTitle}>
								<AvatarItemText>{jobTitle}</AvatarItemText>
							</Tooltip>
						)
					}
					label={label}
				/>
			</AvatarItemContainer>
			{numberOfTotalReports > 0 && numberOfDirectReports > 0 && (
				<Badge appearance={selected ? 'primary' : 'default'} max={99999}>
					{numberOfTotalReports}
				</Badge>
			)}
		</ChartCard>
	);

	if (cloudId && requestingUserId) {
		return (
			<UserProfileCardWithTrigger
				accountId={user.accountId}
				cloudId={cloudId}
				requestingUserId={requestingUserId}
				trigger="hover"
			>
				{card}
			</UserProfileCardWithTrigger>
		);
	}

	return card;
}
